import React, { useState, useEffect } from "react";
import { useTimelineDiagram } from "./hooks/useTimelineDiagram";
import TimelineChart from "./components/TimelineChart";
import { DEFAULT_METRIC, GRANULARITY_OPTIONS } from "./constants";
import BaseTile from '../../../components/Tile/BaseTile';
import TileHeader, { TileHeaderButton } from '../../../components/Tile/TileHeader';
import TileContent, { TileStatusContent } from '../../../components/Tile/TileContent';
import { useAutoLoad } from '../../../hooks/useAutoLoad';
import { cn } from '../../../utils/cn';

// FilterSection Component (Unchanged)
const FilterSection = ({ 
  inputs, 
  handleInputChange, 
  applyFilters, 
  objectTypes, 
  loading,
  globalDateRange 
}) => (
  <div className="bg-gray-50 border border-gray-200 rounded-md p-4 space-y-4">
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Start Date */}
      <div className="flex flex-col">
        <label className="text-sm font-medium text-gray-700 mb-1">
          Start Date
        </label>
        <input
          type="date"
          name="startDate"
          value={inputs.startDate}
          onChange={handleInputChange}
          disabled={loading}
          min={globalDateRange?.startDate}
          max={globalDateRange?.endDate}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
      </div>

      {/* End Date */}
      <div className="flex flex-col">
        <label className="text-sm font-medium text-gray-700 mb-1">
          End Date
        </label>
        <input
          type="date"
          name="endDate"
          value={inputs.endDate}
          onChange={handleInputChange}
          disabled={loading}
          min={globalDateRange?.startDate}
          max={globalDateRange?.endDate}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
      </div>

      {/* Granularity */}
      <div className="flex flex-col">
        <label className="text-sm font-medium text-gray-700 mb-1">
          Granularity
        </label>
        <select
          name="granularity"
          value={inputs.granularity}
          onChange={handleInputChange}
          disabled={loading}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          {GRANULARITY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Object Type */}
      <div className="flex flex-col">
        <label className="text-sm font-medium text-gray-700 mb-1">
          Object Type
        </label>
        <select
          name="objectType"
          value={inputs.objectType}
          onChange={handleInputChange}
          disabled={loading || !objectTypes.length}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          <option value="">-- Select Object Type --</option>
          {objectTypes.map((ot) => (
            <option key={ot} value={ot}>
              {ot}
            </option>
          ))}
        </select>
      </div>
    </div>

    {/* Apply Button */}
    <div className="flex justify-center pt-2">
      <button
        onClick={applyFilters}
        disabled={loading}
        className={cn(
          "px-6 py-2 text-sm font-medium rounded-md transition-colors duration-200",
          "focus:outline-none focus:ring-2 focus:ring-offset-2",
          loading
            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
            : "bg-success-500 text-white hover:bg-success-600 focus:ring-success-500"
        )}
      >
        {loading ? "Loading..." : "Load Chart"}
      </button>
    </div>
  </div>
);


const TimelineDiagram = ({ activeFile, onRefresh }) => {
  const [showFilters, setShowFilters] = useState(false);
  const {
    objectTypes,
    timelineData,
    loading,
    error,
    filters,
    setFilters,
    isMetadataLoaded,
    fetchObjectTypes,
    globalDateRange,
  } = useTimelineDiagram(DEFAULT_METRIC, activeFile);

  const [inputs, setInputs] = useState({
    startDate: filters.startDate,
    endDate: filters.endDate,
    granularity: "daily",
    objectType: filters.objectType,
  });

  useEffect(() => {
    setInputs({ startDate: "", endDate: "", granularity: "daily", objectType: "" });
    setShowFilters(false);
  }, [activeFile]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    setFilters.setStartDate(inputs.startDate);
    setFilters.setEndDate(inputs.endDate);
    setFilters.setGranularity(inputs.granularity);
    setFilters.setObjectType(inputs.objectType);
  };

  const handleConfigureClick = async () => {
    await fetchObjectTypes();
    setShowFilters(true);
  };

  // Check if global filters are applied
  const hasGlobalFilters = globalDateRange || (objectTypes.length > 0 && objectTypes.length < 10); // Assuming there are more than 10 total object types
  
  const hasAppliedFilters = !!filters.startDate && !!filters.objectType;
  const showNoData = isMetadataLoaded && !loading && !error && timelineData.length === 0 && hasAppliedFilters;
  const showChart = isMetadataLoaded && !loading && !error && timelineData.length > 0;
  const hasData = isMetadataLoaded || showChart;

  // Auto-load data when persistent cache is enabled
  useAutoLoad(fetchObjectTypes, [activeFile], hasData);

  // Provide refresh function to parent (GridComponent)
  React.useEffect(() => {
    if (onRefresh) {
      onRefresh(fetchObjectTypes);
    }
  }, [onRefresh, fetchObjectTypes]);

  return (
    <BaseTile
      loading={loading && isMetadataLoaded}
      error={error}
      minHeight="lg"
      className="h-full"
    >
      {isMetadataLoaded && (
        <TileHeader
          layout="between"
          size="default"
          actions={
            <TileHeaderButton
              variant="primary"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="bg-info-500 hover:bg-info-600 focus:ring-info-500"
            >
              {showFilters ? "Hide Filters" : "Show Filters"}
            </TileHeaderButton>
          }
        />
      )}

      <TileContent layout="flex" direction="column" gap="lg" grow>
        {loading && !isMetadataLoaded ? (
          <TileStatusContent status="loading" title="Loading Configuration..." />
        ) : !isMetadataLoaded && !error ? (
          <TileStatusContent
            status="info"
            title="Configure Timeline"
            message="Load options to set up and view the timeline chart."
            action={
              <button
                onClick={handleConfigureClick}
                className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600"
              >
                Configure Chart
              </button>
            }
            icon={<svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" /></svg>}
          />
        ) : (
          <>
            {showFilters && (
              <FilterSection
                inputs={inputs}
                handleInputChange={handleInputChange}
                applyFilters={applyFilters}
                objectTypes={objectTypes}
                loading={loading}
                globalDateRange={globalDateRange}
              />
            )}

            {showChart && (
              <div className="mt-5 w-full flex-grow">
                <TimelineChart data={timelineData} metric={DEFAULT_METRIC} />
              </div>
            )}

            {showNoData && (
              <TileStatusContent
                status="empty"
                title="No Data Available"
                message="No data found for the selected parameters."
              />
            )}
            
            {isMetadataLoaded && showFilters && !hasAppliedFilters && !loading && (
               <TileStatusContent
                status="info"
                title="Select Filters"
                message="Choose parameters and click 'Load Chart' to see the data."
              />
            )}
          </>
        )}
      </TileContent>
    </BaseTile>
  );
};

export default TimelineDiagram;